import Config from "src/config";
import { api } from "../MainService/api";
import {
  KnowledgeBaseResponse,
  GetLevelsResponse,
  GetSubjectsResponse,
  UploadFileResponse,
  KnowledgeBaseService,
  CombinedSummaryResponse,
  CombinedSummaryError,
  UploadFileResponseType,
  UploadFileSuccess,
  UploadFileError,
} from "./knowledgeBase.types";
import {
  FileType,
  FileUploadMap,
  FileUrlMap,
  KnowledgeBaseDocumentType,
  SubjectType,
} from "src/interfaces/KnowledgeBase";

const KNOWLEDGE_BASE_URL = Config.ADMIN_URL;

export const knowledgeBaseService: KnowledgeBaseService = {
  getLevels: async () => {
    try {
      const response = await api.apisauce.get<
        Array<{
          id: number;
          name: string;
          description: string | null;
          order_num: number;
        }>
      >(`${KNOWLEDGE_BASE_URL}/grade-levels`);

      if (!response.ok) {
        return { kind: "bad-data" as const };
      }

      // Return all level fields including description and order_num
      const levels = response.data || [];
      return { kind: "ok" as const, data: levels };
    } catch (error) {
      console.error("Error fetching levels:", error);
      return { kind: "bad-data" as const };
    }
  },

  getSubjects: async (levelId: number) => {
    try {
      const response = await api.apisauce.get<Array<SubjectType>>(
        `${KNOWLEDGE_BASE_URL}/subjects?level_id=${levelId}`
      );

      if (!response.ok) {
        return { kind: "bad-data" as const };
      }

      const data =
        response.data?.map((subject) => ({
          id: subject.id,
          name: subject.name,
          description: subject.description,
          is_required: subject.is_required,
          structure_kb: subject.structure_kb,
          structure_qb: subject.structure_qb,
          is_kb_required: subject.is_kb_required,
          is_qb_required: subject.is_qb_required,
        })) || [];

      return { kind: "ok" as const, data: data };
    } catch (error) {
      console.error("Error fetching subjects:", error);
      return { kind: "bad-data" as const };
    }
  },

  uploadFile: async (
    file: File,
    type: FileType,
    level: string,
    subject: string
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);
    formData.append("level", level);
    formData.append("subject", subject);

    try {
      const response = await api.apisauce.post<{
        success: boolean;
        message: string;
        filePath?: string;
      }>(`${KNOWLEDGE_BASE_URL}/knowledge-base/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.ok || !response.data?.success) {
        return {
          kind: "bad-data" as const,
          message: response.data?.message || "Failed to upload file",
        };
      }

      return {
        kind: "ok" as const,
        data: {
          success: true,
          message: response.data.message,
          filePath: response.data.filePath,
        },
      };
    } catch (error) {
      console.error("Error uploading file:", error);
      return {
        kind: "bad-data" as const,
        message: "An unexpected error occurred during file upload",
      };
    }
  },

  getFilesList: async () => {
    try {
      const response = await api.apisauce.get<KnowledgeBaseDocumentType[]>(
        `${KNOWLEDGE_BASE_URL}/knowledge-base/list-files`
      );

      if (!response.ok) {
        return {
          kind: "bad-data" as const,
          message: "Failed to get files list",
        };
      }

      return {
        kind: "ok" as const,
        data: response.data,
      };
    } catch (error) {
      console.error("Error getting files list:", error);
      return {
        kind: "bad-data" as const,
        message: "An unexpected error occurred during file list retrieval",
      };
    }
  },

  uploadManualFile: async (
    is_qb: boolean,
    level: string,
    subject: string,
    summary: string,
    file_name: string,
    is_from_file: boolean
  ): Promise<UploadFileResponseType> => {
    try {
      const response = await api.apisauce.post<
        UploadFileSuccess | UploadFileError
      >(`${KNOWLEDGE_BASE_URL}/knowledge-base/submit-file`, {
        is_qb,
        level,
        subject,
        file_name,
        summary,
        is_from_file,
      });

      if (!response.ok) {
        const errorData = response.data as UploadFileError;
        return {
          kind: "bad-data",
          message: errorData?.detail || "Failed to upload manual file",
        };
      }

      return {
        kind: "ok",
        data: response.data as UploadFileSuccess,
      };
    } catch (error) {
      console.error("Error uploading manual file:", error);
      return {
        kind: "bad-data",
        message: "An unexpected error occurred during manual file upload",
      };
    }
  },

  modifyFile: async (
    is_qb: boolean,
    level: string,
    subject: string,
    summary: string,
    file_name: string
  ) => {
    try {
      const response = await api.apisauce.put(
        `${KNOWLEDGE_BASE_URL}/knowledge-base/modify-file`,
        {
          file_name,
          is_qb,
          level,
          subject,
          summary,
        }
      );

      if (!response.ok) {
        return {
          kind: "bad-data" as const,
          message: "Failed to modify file",
        };
      }

      return {
        kind: "ok" as const,
        data: {
          success: true,
          message: "File modified successfully",
        },
      };
    } catch (error) {
      console.error("Error modifying file:", error);
      return {
        kind: "bad-data" as const,
        message: "An unexpected error occurred during file modification",
      };
    }
  },

  deleteFile: async (
    file_name: string,
    subject: string,
    is_qb: boolean = false
  ) => {
    try {
      const response = await api.apisauce.delete(
        `${KNOWLEDGE_BASE_URL}/knowledge-base/delete-file?file_name=${file_name}&subject=${subject}&is_qb=${is_qb}`
      );

      if (!response.ok) {
        return {
          kind: "bad-data" as const,
          message: "Failed to delete file",
        };
      }

      return {
        kind: "ok" as const,
        data: {
          success: true,
          message: "File deleted successfully",
        },
      };
    } catch (error) {
      console.error("Error deleting file:", error);
      return {
        kind: "bad-data" as const,
        message: "An unexpected error occurred during file deletion",
      };
    }
  },

  parseFile: async (
    file: File,
    level: string,
    subject: string,
    is_qb: boolean
  ): Promise<KnowledgeBaseResponse<CombinedSummaryResponse>> => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("level", level);
      formData.append("subject", subject);
      formData.append("is_qb", is_qb.toString());
      const response = await api.apisauce.post<
        CombinedSummaryResponse | CombinedSummaryError
      >(`${KNOWLEDGE_BASE_URL}/knowledge-base/upload-file`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 0,
      });

      if (!response.ok) {
        return { kind: "bad-data" as const, message: response.data?.detail };
      }

      const data = response.data;

      return { kind: "ok" as const, data: data as CombinedSummaryResponse };
    } catch (error) {
      console.error("Error fetching subjects:", error);
      return { kind: "bad-data" as const, message: String(error) };
    }
  },

  getCombinedSummary: async (
    level: string,
    subject: string
  ): Promise<KnowledgeBaseResponse<CombinedSummaryResponse>> => {
    try {
      const response = await api.apisauce.get<
        CombinedSummaryResponse | CombinedSummaryError
      >(
        `${KNOWLEDGE_BASE_URL}/knowledge-base/combined-summary?level=${level}&subject=${subject}`
      );

      if (!response.ok) {
        const errorData = response.data as CombinedSummaryError;
        return { kind: "bad-data", message: errorData?.detail };
      }

      return { kind: "ok", data: response.data as CombinedSummaryResponse };
    } catch (error) {
      console.error("Error fetching subjects:", error);
      return { kind: "bad-data", message: String(error) };
    }
  },

  getUrlsToPostOnAzure: async (
    fileName: string,
    fileIds: string[]
  ): Promise<{ kind: "ok"; data: FileUrlMap } | { kind: "bad-data" }> => {
    try {
      const response = await api.apisauce.post<FileUrlMap>(
        `${KNOWLEDGE_BASE_URL}/knowledge-base/get-image-upload-and-read-urls`,
        { file_name: fileName, question_id_list: fileIds }
      );

      if (!response.ok || !response.data) {
        return { kind: "bad-data" as const };
      }

      return { kind: "ok" as const, data: response.data };
    } catch (error) {
      console.error("Error fetching URLs:", error);
      return { kind: "bad-data" as const };
    }
  },

  uploadFilesToAzure: async (
    files: FileUploadMap
  ): Promise<{ kind: "ok" } | { kind: "bad-data" }> => {
    try {
      const uploadPromises = Object.entries(files).map(
        async ([uuid, { uploadUrl, file }]) => {
          const res = await fetch(uploadUrl, {
            method: "PUT",
            body: file,
            headers: {
              "x-ms-blob-type": "BlockBlob", // required for Azure Blob upload
            },
          });

          if (!res.ok) {
            throw new Error(`Upload failed for ${uuid}`);
          }
        }
      );

      await Promise.all(uploadPromises);

      return { kind: "ok" as const };
    } catch (err) {
      console.error("Azure upload failed:", err);
      return { kind: "bad-data" as const };
    }
  },
};
